import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Input } from '@/ui/Field';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  useBorrarRol, useCatalogoDeRoles, useGuardarRol, useRol, useRoles, type Rol,
} from './api';

/**
 * Roles y permisos — la matriz de módulo × acción.
 *
 * ## Lo que cambia
 *
 * - **Borrar un rol con gente dentro ya no depende del navegador.** El vanilla
 *   pasaba el nombre y el número de usuarios por la URL del `onclick`
 *   (`deleteRole(${r.id},'${r.name.replace(/'/g,"")}',${r.user_count})`), así que
 *   un rol llamado «Líder d'Área» perdía el apóstrofo al pasar por ahí y el
 *   recuento venía del HTML que se pintó, no del servidor.
 * - **La matriz se lee con lector de pantalla.** Eran casillas sin etiqueta
 *   dentro de celdas: «casilla, casilla, casilla» treinta veces. Cada una dice
 *   ahora su módulo y su acción.
 * - **Marcar una fila o una columna entera** es de lo primero que se pide al
 *   crear un rol, y no existía: había que pulsar las casillas una por una.
 */
export function Roles() {
  const consulta = useRoles();
  const [editando, setEditando] = useState<number | null | undefined>(undefined);
  const [borrando, setBorrando] = useState<Rol | null>(null);
  const borrar = useBorrarRol();

  const roles = consulta.data ?? [];

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-4 font-bold text-brand-ink">Roles y permisos</h2>
          <p className="mt-1 text-3 text-muted">
            Define qué módulos y acciones tiene cada rol. Los cambios aplican a todos sus
            usuarios, actuales y futuros.
          </p>
        </div>
        <Button size="sm" onClick={() => setEditando(null)}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> Crear rol
        </Button>
      </header>

      {consulta.isPending ? (
        <SkeletonTarjetas n={4} />
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="los roles y permisos"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !roles.length ? (
        <EstadoVacio icono="bi-person-badge" titulo="Sin roles" />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {roles.map((r) => (
            <li key={r.id} className="flex">
              <Card className="flex w-full flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-3 font-bold text-brand-ink">
                      {r.name}
                      {r.is_admin ? (
                        <Badge tone="danger">Total</Badge>
                      ) : r.is_staff ? (
                        <Badge tone="info">Gestión</Badge>
                      ) : null}
                      {r.area_scoped && <Badge tone="neutral">Solo su área</Badge>}
                    </p>
                    <p className="text-2 text-muted">
                      {r.key}{r.is_system ? ' · sistema' : ''}
                    </p>
                  </div>
                  <Badge tone="neutral">
                    {r.user_count} {r.user_count === 1 ? 'usuario' : 'usuarios'}
                  </Badge>
                </div>

                {r.description && <p className="mt-2 text-2 text-muted">{r.description}</p>}

                <div className="mt-auto flex gap-2 pt-3">
                  <Button variant="ghost" size="sm" onClick={() => setEditando(r.id)}>
                    <i className="bi bi-sliders" aria-hidden="true" /> Permisos
                  </Button>
                  {!r.is_system && !r.is_admin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBorrando(r)}
                      // El recuento viene del servidor, no del HTML que se
                      // pintó. `razonDeshabilitado` NO apaga por su cuenta: solo
                      // explica un botón que ya está apagado.
                      disabled={(r.user_count ?? 0) > 0}
                      razonDeshabilitado={
                        `Este rol tiene ${r.user_count} ${(r.user_count ?? 0) === 1
                          ? 'usuario. Reasígnalo' : 'usuarios. Reasígnalos'} antes de eliminarlo.`
                      }
                    >
                      <i className="bi bi-trash text-danger-ink" aria-hidden="true" />
                      <span className="sr-only">Eliminar el rol {r.name}</span>
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {editando !== undefined && (
        <EditorDeRol id={editando} onCerrar={() => setEditando(undefined)} />
      )}

      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar rol"
        descripcion={`¿Eliminar el rol «${borrando?.name ?? ''}»? Sus permisos se pierden.`}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!borrando) return;
          borrar.mutate(borrando.id, {
            onSuccess: () => { Utils.toast('Rol eliminado', 'success'); setBorrando(null); },
            onError: (e) => Utils.toast((e as Error).message, 'error'),
          });
        }}
      />
    </>
  );
}

/** Una celda de la matriz vale `true` si ese módulo tiene esa acción. */
type Matriz = Record<string, Set<string>>;

function matrizDesde(permisos?: Record<string, string[]> | null): Matriz {
  const m: Matriz = {};
  for (const [modulo, acciones] of Object.entries(permisos ?? {})) {
    m[modulo] = new Set(acciones);
  }
  return m;
}

function EditorDeRol({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const catalogo = useCatalogoDeRoles(true);
  const ficha = useRol(id);
  const guardar = useGuardarRol();

  const rol = ficha.data;
  const cargando = catalogo.isPending || (id != null && ficha.isPending);

  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [gestion, setGestion] = useState(false);
  const [soloSuArea, setSoloSuArea] = useState(false);
  const [matriz, setMatriz] = useState<Matriz>({});
  const [listo, setListo] = useState(false);

  // Se copia la ficha al estado una sola vez, cuando llega. Mientras se escribe,
  // manda lo escrito.
  if (!listo && !cargando) {
    setNombre(rol?.name ?? '');
    setDescripcion(rol?.description ?? '');
    setGestion(Boolean(rol?.is_staff));
    setSoloSuArea(Boolean(rol?.area_scoped));
    setMatriz(matrizDesde(rol?.permissions));
    setListo(true);
  }

  const modulos = catalogo.data?.modules ?? [];
  const acciones = catalogo.data?.actions ?? [];
  // El rol Administrador tiene acceso total por definición: su matriz no se
  // edita porque no la consulta nadie.
  const bloqueado = Boolean(rol?.is_admin);

  function marcada(modulo: string, accion: string): boolean {
    return matriz[modulo]?.has(accion) ?? false;
  }

  function alternar(modulo: string, accion: string, valor: boolean) {
    setMatriz((previa) => {
      const copia: Matriz = {};
      for (const [k, v] of Object.entries(previa)) copia[k] = new Set(v);
      const fila = copia[modulo] ?? new Set<string>();
      if (valor) fila.add(accion); else fila.delete(accion);
      copia[modulo] = fila;
      return copia;
    });
  }

  /** Marcar o desmarcar una fila (un módulo) o una columna (una acción). */
  function alternarFila(modulo: string, valor: boolean) {
    for (const a of acciones) alternar(modulo, a.key, valor);
  }
  function alternarColumna(accion: string, valor: boolean) {
    for (const m of modulos) alternar(m.key, accion, valor);
  }

  function enviar() {
    const limpio = nombre.trim();
    if (!limpio) { Utils.toast('El nombre es obligatorio', 'warning'); return; }
    guardar.mutate({
      id,
      cuerpo: {
        name: limpio,
        description: descripcion.trim(),
        is_staff: gestion,
        area_scoped: soloSuArea,
        permissions: Object.entries(matriz)
          .filter(([, acc]) => acc.size > 0)
          .map(([module_key, acc]) => ({ module_key, actions: [...acc] })),
      },
    }, {
      onSuccess: () => {
        Utils.toast('Rol guardado. Aplica a todos sus usuarios.', 'success');
        onCerrar();
      },
      onError: (e) => Utils.toast((e as Error).message, 'error'),
    });
  }

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      ancho="max-w-4xl"
      titulo={id ? 'Editar rol' : 'Crear rol'}
      descripcion="Los cambios aplican a todos sus usuarios, actuales y futuros."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          {!bloqueado && (
            <Button onClick={enviar} loading={guardar.isPending}>Guardar</Button>
          )}
        </>
      }
    >
      {cargando ? (
        <SkeletonTarjetas n={2} />
      ) : catalogo.isError ? (
        <EstadoError
          err={catalogo.error}
          que="el catálogo de módulos y acciones"
          onReintentar={() => void catalogo.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <Field label="Nombre del rol">
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              readOnly={Boolean(rol?.is_system)}
              placeholder="Ej. Líder de Área"
            />
          </Field>
          <Field label="Descripción" opcional>
            <Input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </Field>

          {!bloqueado && (
            <>
              <label className="flex items-start gap-2 text-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={gestion}
                  onChange={(e) => setGestion(e.target.checked)}
                />
                <span>
                  <strong>Rol de gestión (Bienestar)</strong>: acceso a herramientas de staff
                  —informes, bandeja de solicitudes, inducción—.
                </span>
              </label>
              <label className="flex items-start gap-2 text-3">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={soloSuArea}
                  onChange={(e) => setSoloSuArea(e.target.checked)}
                />
                <span>
                  <strong>Acotar a su propia área</strong>: en solicitudes, informes e inducción
                  solo verá y gestionará los datos de SU área.
                </span>
              </label>
            </>
          )}

          <p className="mt-1 text-3 font-semibold text-brand-ink">Permisos por módulo</p>
          {bloqueado ? (
            <p className="rounded-sm border border-line bg-sunken p-3 text-2 text-muted">
              El rol Administrador tiene acceso total y no se limita.
            </p>
          ) : (
            <MatrizDePermisos
              modulos={modulos}
              acciones={acciones}
              marcada={marcada}
              onAlternar={alternar}
              onAlternarFila={alternarFila}
              onAlternarColumna={alternarColumna}
            />
          )}
        </div>
      )}
    </Dialog>
  );
}

function MatrizDePermisos({
  modulos, acciones, marcada, onAlternar, onAlternarFila, onAlternarColumna,
}: {
  modulos: Array<{ key: string; label: string }>;
  acciones: Array<{ key: string; label: string }>;
  marcada: (m: string, a: string) => boolean;
  onAlternar: (m: string, a: string, v: boolean) => void;
  onAlternarFila: (m: string, v: boolean) => void;
  onAlternarColumna: (a: string, v: boolean) => void;
}) {
  const filaLlena = (m: string) => acciones.every((a) => marcada(m, a.key));
  const columnaLlena = (a: string) => modulos.every((m) => marcada(m.key, a));

  return (
    <div className="max-h-[46vh] overflow-auto rounded-sm border border-line">
      <table className="w-full border-collapse text-2">
        <caption className="sr-only">
          Permisos del rol: una fila por módulo y una columna por acción.
        </caption>
        <thead className="sticky top-0 bg-sunken">
          <tr>
            <th scope="col" className="p-2 text-left font-semibold text-ink">Módulo</th>
            {acciones.map((a) => (
              <th key={a.key} scope="col" className="p-2 text-center font-semibold text-ink">
                <span className="block">{a.label}</span>
                {/* Marcar una columna entera: lo primero que se necesita al
                    crear un rol de solo lectura. */}
                <button
                  type="button"
                  className="mt-0.5 text-1 font-normal text-brand-ink hover:underline focus-visible:focus-anillo"
                  onClick={() => onAlternarColumna(a.key, !columnaLlena(a.key))}
                >
                  {columnaLlena(a.key) ? 'ninguno' : 'todos'}
                  <span className="sr-only">
                    {' '}los módulos para la acción {a.label}
                  </span>
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {modulos.map((m) => (
            <tr key={m.key} className="border-t border-line">
              <th scope="row" className="p-2 text-left font-semibold text-ink">
                {m.label}
                <button
                  type="button"
                  className="ml-2 text-1 font-normal text-brand-ink hover:underline focus-visible:focus-anillo"
                  onClick={() => onAlternarFila(m.key, !filaLlena(m.key))}
                >
                  {filaLlena(m.key) ? 'ninguna' : 'todas'}
                  <span className="sr-only"> las acciones de {m.label}</span>
                </button>
              </th>
              {acciones.map((a) => (
                <td key={a.key} className="p-2 text-center">
                  <input
                    type="checkbox"
                    checked={marcada(m.key, a.key)}
                    onChange={(e) => onAlternar(m.key, a.key, e.target.checked)}
                    // Sin esto la matriz se lee «casilla, casilla, casilla…»
                    // treinta veces y no hay forma de saber cuál se está marcando.
                    aria-label={`${a.label} en ${m.label}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
